/**
 * Supermicro SYS-521GE-TNRT Server Profile
 *
 * Source: research/server-specs.md
 * - 5U form factor
 * - Up to 10 PCIe GPUs (H100 NVL, L40S, RTX 6000 Ada)
 * - Dual-Root PCIe topology
 * - Dual Intel Xeon Scalable (up to 64 cores each)
 * - Up to 8TB DDR5
 * - 4x 2700W PSU (2+2 redundancy)
 * - Extended thermal capacity
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Supermicro SYS-521GE-TNRT with L40S (8-GPU)
 */
export const SUPERMICRO_SYS521GE_L40S_8GPU: ServerProfile = {
  id: 'supermicro-sys521ge-l40s-8gpu',
  name: 'Supermicro SYS-521GE-TNRT (8x L40S)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-521GE-TNRT',

  cpu: {
    sockets: 2,
    coresPerSocket: 48,
    model: 'Intel Xeon Gold 6548Y+ (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.5,
    turboClockGHz: 4.1,
  },

  memory: {
    totalGB: 1536,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 48,
  },

  gpu: {
    platformId: 'l40s',
    gpusPerNode: 8,
  },

  expansion: {
    pcieSlots: [
      // 13 PCIe Gen 5.0 x16 FHFL slots
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 24, // 8 SATA + 8 NVMe + 8 SATA/SAS
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 2, // Dual 10Gb/s Intel X710
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 10800,
    typicalPowerWatts: 4500,
    idlePowerWatts: 400,
    redundancy: 'N+1',
    psuCount: 4,
    psuCapacityWatts: 2700,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 5,
    widthMm: 438,
    depthMm: 786,
    heightMm: 222.5,
    weightKg: 65,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 15354, // 4500W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 75,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/5u/sys-521ge-tnrt',
    notes: '5U server with 8x L40S GPUs for AI inference and graphics workloads',
    validated: true,
  },
} as const;

/**
 * Supermicro SYS-521GE-TNRT with L40S (10-GPU)
 */
export const SUPERMICRO_SYS521GE_L40S_10GPU: ServerProfile = {
  id: 'supermicro-sys521ge-l40s-10gpu',
  name: 'Supermicro SYS-521GE-TNRT (10x L40S)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-521GE-TNRT',

  cpu: {
    sockets: 2,
    coresPerSocket: 48,
    model: 'Intel Xeon Gold 6548Y+ (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.5,
    turboClockGHz: 4.1,
  },

  memory: {
    totalGB: 1536,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 48,
  },

  gpu: {
    platformId: 'l40s',
    gpusPerNode: 10,
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 24,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 2,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 10800,
    typicalPowerWatts: 5000,
    idlePowerWatts: 400,
    redundancy: 'N+1',
    psuCount: 4,
    psuCapacityWatts: 2700,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 5,
    widthMm: 438,
    depthMm: 786,
    heightMm: 222.5,
    weightKg: 70,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 17060, // 5000W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 78,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/5u/sys-521ge-tnrt',
    notes: '5U server with maximum 10x L40S GPUs using dual-root PCIe topology',
    validated: true,
  },
} as const;

/**
 * Supermicro SYS-521GE-TNRT with RTX 6000 Ada (8-GPU)
 */
export const SUPERMICRO_SYS521GE_RTX6000: ServerProfile = {
  id: 'supermicro-sys521ge-rtx6000',
  name: 'Supermicro SYS-521GE-TNRT (8x RTX 6000 Ada)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-521GE-TNRT',

  cpu: {
    sockets: 2,
    coresPerSocket: 32,
    model: 'Intel Xeon Gold 6542Y (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.9,
    turboClockGHz: 4.0,
  },

  memory: {
    totalGB: 1024,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 32,
  },

  gpu: {
    platformId: 'rtx-6000-ada',
    gpusPerNode: 8,
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 16,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 2,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 10800,
    typicalPowerWatts: 3400,
    idlePowerWatts: 350,
    redundancy: 'N+1',
    psuCount: 4,
    psuCapacityWatts: 2700,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 5,
    widthMm: 438,
    depthMm: 786,
    heightMm: 222.5,
    weightKg: 62,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 11601, // 3400W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 72,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/5u/sys-521ge-tnrt',
    notes: '5U server with 8x RTX 6000 Ada for professional visualization and AI',
    validated: true,
  },
} as const;
